import {
  type ReactElement,
  type SubmitEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Check, Loader2, X } from 'lucide-react'
import type { ProductBomImportPreview } from '@/shared/hooks/api/catalog'
import {
  bomImportRowStatusTooltipText,
  evaluateBomImportRows,
  isBomImportMappingSelectionComplete,
  type EvaluatedBomImportRow,
} from '@/shared/lib/bomImportRowValidation'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

const NAME_NONE_VALUE = 'none'

export type BomImportReviewDialogLabels = {
  title: string
  description: string
  requiredFieldsHelper: string
  skuLabel: string
  supplierLabel: string
  nameLabel: string
  placeholder: string
  nameNone: string
  columnFallback: (index: number) => string
  rowColumn: string
  statusColumn: string
  statusPending: string
  statusWillImport: string
  statusSkuRequired: string
  statusSupplierRequired: string
  statusSkuAndSupplierRequired: string
  summary: (invalidCount: number, totalCount: number) => string
  columnsMustDiffer: string
  skuMappingRequired: string
  supplierMappingRequired: string
  cancel: string
  import: string
  importPartial: (count: number) => string
}

type BomImportReviewDialogProps = {
  preview: ProductBomImportPreview | null
  isPending: boolean
  labels: BomImportReviewDialogLabels
  onOpenChange: (open: boolean) => void
  onConfirm: (input: {
    mapping: {
      skuColumn: number
      supplierColumn: number
      nameColumn?: number | null
    }
    importRowNumbers: number[]
  }) => void
}

function columnOptionLabel(
  column: { index: number; header: string },
  fallback: string
): string {
  const header = column.header.trim()
  if (header === '') {
    return fallback
  }
  return `${column.index}. ${header}`
}

function cellDisplayValue(cells: string[], columnIndex: number): string {
  if (columnIndex < 1) {
    return ''
  }
  return cells[columnIndex - 1] ?? ''
}

function RowStatusCell({
  evaluated,
  isValid,
  tooltipText,
}: {
  evaluated: boolean
  isValid: boolean
  tooltipText: string
}): ReactElement {
  let indicator: ReactElement
  if (!evaluated) {
    indicator = (
      <span className="inline-flex size-4 items-center justify-center text-muted-foreground">
        —
      </span>
    )
  } else if (isValid) {
    indicator = (
      <Check
        className="size-4 shrink-0 text-green-600 dark:text-green-500"
        aria-hidden
      />
    )
  } else {
    indicator = (
      <X className="size-4 shrink-0 text-destructive" aria-hidden />
    )
  }

  if (evaluated && !isValid) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex size-7 items-center justify-center rounded-sm hover:bg-muted/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            aria-label={tooltipText}
          >
            {indicator}
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <span className="inline-flex size-7 items-center justify-center">
      {indicator}
    </span>
  )
}

export function BomImportReviewDialog({
  preview,
  isPending,
  labels,
  onOpenChange,
  onConfirm,
}: BomImportReviewDialogProps): ReactElement {
  const [skuColumn, setSkuColumn] = useState('')
  const [supplierColumn, setSupplierColumn] = useState('')
  const [nameColumn, setNameColumn] = useState(NAME_NONE_VALUE)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)

  const isOpen = preview != null

  useEffect(() => {
    if (preview == null) {
      return
    }

    if (preview.suggestedMapping.skuColumn != null) {
      setSkuColumn(String(preview.suggestedMapping.skuColumn))
    } else {
      setSkuColumn('')
    }

    if (preview.suggestedMapping.supplierColumn != null) {
      setSupplierColumn(String(preview.suggestedMapping.supplierColumn))
    } else {
      setSupplierColumn('')
    }

    if (preview.suggestedMapping.nameColumn != null) {
      setNameColumn(String(preview.suggestedMapping.nameColumn))
    } else {
      setNameColumn(NAME_NONE_VALUE)
    }

    setValidationMessage(null)
  }, [preview])

  const columns = preview?.columns ?? []
  const previewRows = preview?.rows ?? []
  const columnSelectItems = columns.map((column) => ({
    value: String(column.index),
    label: columnOptionLabel(column, labels.columnFallback(column.index)),
  }))
  const nameSelectItems = [
    { value: NAME_NONE_VALUE, label: labels.nameNone },
    ...columnSelectItems,
  ]

  const mappingSelection = isBomImportMappingSelectionComplete({
    skuColumn,
    supplierColumn,
    nameColumn,
    nameNoneValue: NAME_NONE_VALUE,
  })

  const mappingComplete = mappingSelection != null

  const evaluatedRows = useMemo(
    () => evaluateBomImportRows(previewRows, mappingSelection),
    [previewRows, mappingSelection]
  )

  const evaluationByRowNumber = useMemo(() => {
    const map = new Map<number, EvaluatedBomImportRow>()
    for (const row of evaluatedRows) {
      map.set(row.rowNumber, row)
    }
    return map
  }, [evaluatedRows])

  const validCount = mappingComplete
    ? evaluatedRows.filter((row) => row.evaluated && row.valid).length
    : 0
  const invalidCount = mappingComplete
    ? evaluatedRows.filter((row) => row.evaluated && !row.valid).length
    : 0

  function isMappedColumn(columnIndex: number): boolean {
    if (mappingSelection == null) {
      return false
    }
    return (
      columnIndex === mappingSelection.skuColumn ||
      columnIndex === mappingSelection.supplierColumn ||
      (mappingSelection.nameColumn != null &&
        columnIndex === mappingSelection.nameColumn)
    )
  }

  function mappedHeaderClass(columnIndex: number): string | undefined {
    if (!isMappedColumn(columnIndex)) {
      return undefined
    }
    return 'bg-sky-100 dark:bg-sky-950/50'
  }

  function selectedColumnLabel(selectedValue: string): string | null {
    if (selectedValue === '') {
      return null
    }
    const selectedItem = columnSelectItems.find(
      (item) => item.value === selectedValue
    )
    return selectedItem?.label ?? null
  }

  let nameSelectLabel: string | null = labels.nameNone
  if (nameColumn !== NAME_NONE_VALUE) {
    nameSelectLabel = selectedColumnLabel(nameColumn)
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()

    if (skuColumn === '') {
      setValidationMessage(labels.skuMappingRequired)
      return
    }

    if (supplierColumn === '') {
      setValidationMessage(labels.supplierMappingRequired)
      return
    }

    if (mappingSelection == null) {
      setValidationMessage(labels.columnsMustDiffer)
      return
    }

    if (validCount === 0) {
      setValidationMessage(labels.summary(invalidCount, evaluatedRows.length))
      return
    }

    setValidationMessage(null)
    onConfirm({
      mapping: mappingSelection,
      importRowNumbers: evaluatedRows
        .filter((row) => row.evaluated && row.valid)
        .map((row) => row.rowNumber),
    })
  }

  const importLabel =
    mappingComplete && invalidCount > 0
      ? labels.importPartial(validCount)
      : labels.import

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden sm:max-w-6xl"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description}</DialogDescription>
        </DialogHeader>
        <form
          className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden"
          onSubmit={handleSubmit}
        >
          <p className="shrink-0 text-sm text-muted-foreground">
            {labels.requiredFieldsHelper}
          </p>
          <div className="grid shrink-0 gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>{labels.skuLabel}</Label>
              <Select
                value={skuColumn}
                onValueChange={(value) => {
                  setSkuColumn(String(value))
                }}
                disabled={isPending}
                items={columnSelectItems}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={labels.placeholder}>
                    {selectedColumnLabel(skuColumn)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {columnSelectItems.map((item) => (
                    <SelectItem key={`sku-${item.value}`} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{labels.supplierLabel}</Label>
              <Select
                value={supplierColumn}
                onValueChange={(value) => {
                  setSupplierColumn(String(value))
                }}
                disabled={isPending}
                items={columnSelectItems}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={labels.placeholder}>
                    {selectedColumnLabel(supplierColumn)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {columnSelectItems.map((item) => (
                    <SelectItem
                      key={`supplier-${item.value}`}
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{labels.nameLabel}</Label>
              <Select
                value={nameColumn}
                onValueChange={(value) => {
                  setNameColumn(String(value))
                }}
                disabled={isPending}
                items={nameSelectItems}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>{nameSelectLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {nameSelectItems.map((item) => (
                    <SelectItem key={`name-${item.value}`} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {validationMessage != null && (
            <p className="shrink-0 text-sm text-destructive">
              {validationMessage}
            </p>
          )}
          {mappingComplete && previewRows.length > 0 && (
            <p className="shrink-0 text-sm font-medium">
              {labels.summary(invalidCount, previewRows.length)}
            </p>
          )}
          <div className="min-h-0 min-w-0 flex-1 overflow-auto rounded-md border">
            <Table className="min-w-max">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-20 w-10 bg-background">
                    {labels.statusColumn}
                  </TableHead>
                  <TableHead className="sticky left-10 z-10 w-16 bg-background">
                    {labels.rowColumn}
                  </TableHead>
                  {columns.map((column) => {
                    const headerLabel = columnOptionLabel(
                      column,
                      labels.columnFallback(column.index)
                    )
                    return (
                      <TableHead
                        key={`head-${column.index}`}
                        className={cn(
                          'max-w-40 truncate',
                          mappedHeaderClass(column.index)
                        )}
                        title={headerLabel}
                      >
                        {headerLabel}
                      </TableHead>
                    )
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((previewRow) => {
                  const evaluation = evaluationByRowNumber.get(
                    previewRow.rowNumber
                  )
                  const evaluated = evaluation?.evaluated === true
                  const isValid = evaluation?.valid === true
                  const tooltipText =
                    evaluation != null
                      ? bomImportRowStatusTooltipText(evaluation, labels)
                      : labels.statusPending
                  const rowInvalid = evaluated && !isValid

                  return (
                    <TableRow
                      key={previewRow.rowNumber}
                      className={cn(
                        'group',
                        rowInvalid &&
                          'bg-destructive/10 hover:bg-destructive/15'
                      )}
                    >
                      <TableCell
                        className={cn(
                          'sticky left-0 z-20 w-10 px-2 transition-colors',
                          rowInvalid
                            ? 'bg-destructive/10 group-hover:bg-destructive/15'
                            : 'bg-background group-hover:bg-muted/50'
                        )}
                      >
                        <RowStatusCell
                          evaluated={evaluated}
                          isValid={isValid}
                          tooltipText={tooltipText}
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          'sticky left-10 z-10 w-16 bg-background transition-colors',
                          rowInvalid
                            ? 'bg-destructive/10 group-hover:bg-destructive/15'
                            : 'bg-background group-hover:bg-muted/50'
                        )}
                      >
                        {previewRow.rowNumber}
                      </TableCell>
                      {columns.map((column) => {
                        const cellValue = cellDisplayValue(
                          previewRow.cells,
                          column.index
                        )
                        return (
                          <TableCell
                            key={`cell-${previewRow.rowNumber}-${column.index}`}
                            className="max-w-40 truncate"
                            title={cellValue}
                          >
                            {cellValue}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              {labels.cancel}
            </Button>
            <Button
              type="submit"
              disabled={
                isPending ||
                mappingSelection == null ||
                validCount === 0
              }
            >
              {isPending && (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              )}
              {importLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
